import React from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import api, { apiError } from "@/lib/api";
import { money } from "@/lib/format";
import { toast } from "sonner";
import { GeoMap } from "@/components/GeoMap";

const TYPE_EMOJI = { bike: "🏍️", car: "🚗", truck: "🚚", van: "🚐" };
const perMile = (c) => `$${((c || 0) / 100).toFixed(2)}`;
const STATUS = {
  moving: { color: "#22c55e", label: "Moving", cls: "bg-emerald-500/15 text-emerald-600" },
  idle: { color: "#f59e0b", label: "Idle", cls: "bg-amber-500/15 text-amber-600" },
  offline: { color: "#94a3b8", label: "Offline", cls: "bg-slate-500/15 text-slate-500" },
};
const ago = (iso) => {
  if (!iso) return "never";
  const s = Math.max(0, (Date.now() - new Date(iso).getTime()) / 1000);
  if (s < 60) return `${Math.round(s)}s ago`;
  if (s < 3600) return `${Math.round(s / 60)}m ago`;
  return `${Math.round(s / 3600)}h ago`;
};

export default function AdminFleet() {
  const qc = useQueryClient();
  const { data } = useQuery({ queryKey: ["admin-fleet"], queryFn: async () => (await api.get("/fleet/inspector/vehicles")).data.vehicles, refetchInterval: 6000 });
  const { data: config } = useQuery({ queryKey: ["fleet-config"], queryFn: async () => (await api.get("/fleet/config")).data });
  const token = config?.mapbox_token;
  const vehicles = data || [];
  const moving = vehicles.filter((v) => v.track?.status === "moving").length;
  const totals = vehicles.reduce((a, v) => ({
    miles: a.miles + (v.metrics?.total_miles || 0),
    fuel: a.fuel + (v.metrics?.fuel_cost_cents || 0),
  }), { miles: 0, fuel: 0 });
  const markers = vehicles.filter((v) => v.track?.lat != null)
    .map((v) => ({ key: v.id, lat: v.track.lat, lng: v.track.lng, color: STATUS[v.track.status].color }));

  const review = async (id, approve) => {
    try { await api.post(`/fleet/inspector/vehicles/${id}/review`, { approve, notes: "" }); toast.success(approve ? "Approved" : "Rejected"); qc.invalidateQueries({ queryKey: ["admin-fleet"] }); }
    catch (e) { toast.error(apiError(e)); }
  };

  return (
    <div className="max-w-4xl space-y-6" data-testid="admin-fleet">
      <div>
        <h1 className="font-display text-2xl font-semibold tracking-tight">iYeneh Fleet</h1>
        <p className="mt-1 text-sm text-muted-foreground">Live B2B vehicle tracking — every company vehicle on the platform, its live position, movement status and fuel efficiency.</p>
      </div>

      <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
        <div className="rounded-2xl border border-white/10 bg-card p-4"><p className="text-lg font-semibold">{vehicles.length}</p><p className="text-xs text-muted-foreground">Vehicles</p></div>
        <div className="rounded-2xl border border-white/10 bg-card p-4" data-testid="admin-fleet-moving"><p className="text-lg font-semibold text-emerald-600">{moving}</p><p className="text-xs text-muted-foreground">Moving now</p></div>
        <div className="rounded-2xl border border-white/10 bg-card p-4"><p className="text-lg font-semibold">{totals.miles.toFixed(0)}</p><p className="text-xs text-muted-foreground">Total miles logged</p></div>
        <div className="rounded-2xl border border-white/10 bg-card p-4"><p className="text-lg font-semibold">{money(totals.fuel)}</p><p className="text-xs text-muted-foreground">Fuel spend logged</p></div>
      </div>

      {token && markers.length > 0 && (
        <div data-testid="admin-fleet-map"><GeoMap token={token} markers={markers} /></div>
      )}

      <div className="space-y-2">
        {vehicles.length === 0 && <p className="rounded-2xl border border-dashed border-white/10 p-8 text-center text-sm text-muted-foreground">No fleet vehicles registered yet.</p>}
        {vehicles.map((v) => {
          const s = STATUS[v.track?.status || "offline"];
          return (
            <div key={v.id} data-testid={`fleet-row-${v.id}`} className="flex flex-wrap items-center justify-between gap-3 rounded-2xl border border-white/10 bg-card p-4">
              <div className="flex items-center gap-3">
                <span className="text-2xl">{TYPE_EMOJI[v.type] || "🚗"}</span>
                <div>
                  <p className="text-sm font-semibold">{v.name} <span className="text-xs text-muted-foreground">· {v.owner_email}</span></p>
                  <p className="text-xs text-muted-foreground">{[v.plate || "no plate", v.driver_email ? `driver ${v.driver_email}` : "no driver", `${((v.track?.distance_today_m || 0) / 1000).toFixed(1)} km today`, `seen ${ago(v.track?.updated_at)}`, v.community].filter(Boolean).join(" · ")}</p>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <span className={`flex items-center gap-1 rounded-full px-2.5 py-1 text-xs font-semibold ${s.cls}`}><span className="h-1.5 w-1.5 rounded-full" style={{ background: s.color }} />{s.label}</span>
                <span className={`rounded-full px-2.5 py-1 text-xs font-semibold ${v.approval_status === "approved" ? "bg-emerald-500/15 text-emerald-600" : v.approval_status === "rejected" ? "bg-destructive/15 text-destructive" : "bg-amber-500/15 text-amber-600"}`}>{v.approval_status}</span>
                {v.approval_status !== "approved" && <button data-testid={`fleet-approve-${v.id}`} onClick={() => review(v.id, true)} className="rounded-full bg-primary px-3 py-1 text-xs font-semibold text-primary-foreground">Approve</button>}
                {v.approval_status !== "rejected" && <button data-testid={`fleet-reject-${v.id}`} onClick={() => review(v.id, false)} className="rounded-full border border-white/10 px-3 py-1 text-xs font-semibold hover:text-destructive">Reject</button>}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
